/**
 * wk-a11y-nav.js
 * ADA fixes for the Symmetry desktop mega menu (Sept. 25, 2026 accessibility audit).
 *
 * Symmetry opens a dropdown in two ways:
 *   1. JS: hover, tap or Enter adds .navigation__item--show-children (assets/main.js, MainNavigation).
 *   2. CSS only: body.tab-used ... .navigation__item--with-children:focus-within shows the panel
 *      as soon as keyboard focus enters the item.
 * Neither path handles Escape, and path 2 never updates aria-expanded.
 *
 * This file:
 *   - Escape closes the open dropdown (WCAG 1.4.13) and returns focus to its top-level link.
 *   - Keeps aria-expanded on the top-level link in sync with what is actually visible (WCAG 4.1.2).
 *   - Closes a JS-opened dropdown when keyboard focus leaves it.
 * It does not change main.js.
 *
 * Product page fixes (Oct. 20, 2026 phase 2 audit) are in the second block at the end of this file.
 */
(function () {
  var ITEM_SELECTOR = '#main-nav > .navigation .navigation__tier-1 > .navigation__item--with-children, '
    + '#proxy-nav .navigation__tier-1 > .navigation__item--with-children';
  var SHOW_CLASS = 'navigation__item--show-children';
  var DISMISSED_CLASS = 'wk-nav-dismissed';

  function injectStyles() {
    if (document.getElementById('wk-a11y-nav-styles')) return;
    var style = document.createElement('style');
    style.id = 'wk-a11y-nav-styles';
    // Suppress the CSS :focus-within reveal after Escape, until the item is opened again.
    style.textContent = 'body.tab-used .navigation .navigation__item--with-children.' + DISMISSED_CLASS
      + ':not(.' + SHOW_CLASS + '):focus-within .navigation__tier-2-container'
      + '{visibility:hidden;opacity:0;pointer-events:none;}';
    document.head.appendChild(style);
  }

  function topLink(item) {
    return item.querySelector(':scope > .navigation__link');
  }

  function focusWithin(item) {
    try {
      return item.matches(':focus-within');
    } catch (e) {
      return item.contains(document.activeElement);
    }
  }

  function isOpen(item) {
    if (item.classList.contains(SHOW_CLASS)) return true;
    return document.body.classList.contains('tab-used')
      && focusWithin(item)
      && !item.classList.contains(DISMISSED_CLASS);
  }

  function syncExpanded(item) {
    var link = topLink(item);
    if (link) link.setAttribute('aria-expanded', isOpen(item) ? 'true' : 'false');
  }

  function closeItem(item, returnFocus) {
    item.classList.add(DISMISSED_CLASS);
    if (item.classList.contains(SHOW_CLASS)) {
      // Use the theme's own close path (clears timers and header state).
      item.dispatchEvent(new Event('mouseleave'));
    }
    var link = topLink(item);
    if (link) {
      link.setAttribute('aria-expanded', 'false');
      if (returnFocus) link.focus();
    }
  }

  function init() {
    var items = document.querySelectorAll(ITEM_SELECTOR);
    if (!items.length) return;
    injectStyles();

    items.forEach(function (item) {
      item.addEventListener('focusin', function () {
        syncExpanded(item);
      });

      item.addEventListener('focusout', function (evt) {
        if (evt.relatedTarget && item.contains(evt.relatedTarget)) return;
        item.classList.remove(DISMISSED_CLASS);
        // A dropdown opened with Enter stays open in the theme until mouseleave; close it when focus leaves.
        if (item.classList.contains(SHOW_CLASS) && !item.matches(':hover')) {
          item.dispatchEvent(new Event('mouseleave'));
        }
        setTimeout(function () { syncExpanded(item); }, 0);
      });

      // Reopening by hover or Enter clears the Escape dismissal.
      item.addEventListener('mouseenter', function () {
        item.classList.remove(DISMISSED_CLASS);
      });

      item.addEventListener('mouseleave', function () {
        // The theme sets aria-expanded=false on mouseleave even if keyboard focus still shows the panel.
        setTimeout(function () { syncExpanded(item); }, 300);
      });
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Escape' && evt.key !== 'Esc') return;
      items.forEach(function (item) {
        if (!isOpen(item)) return;
        var hadFocus = item.contains(document.activeElement);
        closeItem(item, hadFocus);
        if (hadFocus) evt.preventDefault();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Product page fixes (Oct. 20, 2026 phase 2 accessibility audit).
 *
 *   - Text-box blocks in sections/main-product.liquid (the "Arrives in 12 days" note): the theme
 *     derives the text color by darkening the box color, which gives 3.54:1 on #e0f4d5. If a box
 *     comes in under 4.5:1, move its text color toward black (or white on dark boxes) until it
 *     reaches 5:1 (WCAG 1.4.3). Boxes that already pass are left alone.
 *   - Gallery thumbnail links (a.thumbnail) wrap an image with alt="" when the product image has
 *     no alt text in Shopify, so they announce as just "link". Give those links "View image N"
 *     (WCAG 2.4.4, 4.1.2). Thumbnails whose image has alt text keep it as their name.
 *
 * Runs on load and again when product markup is added later (quick buy, theme editor).
 */
(function () {
  var MIN_RATIO = 4.5;
  // Aim a little above the minimum so rounding in the browser can't drop it back under 4.5:1.
  var TARGET_RATIO = 5;

  function parseColor(value) {
    var str = String(value || '').trim();
    var hex = str.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
      var h = hex[1].length === 3 ? hex[1].replace(/(.)/g, '$1$1') : hex[1];
      var n = parseInt(h, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    var nums = str.match(/\d+(\.\d+)?/g);
    if (nums && nums.length >= 3) return [Number(nums[0]), Number(nums[1]), Number(nums[2])];
    return null;
  }

  function luminance(rgb) {
    var c = rgb.map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function contrast(a, b) {
    var la = luminance(a);
    var lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  function fixTextBox(box) {
    if (box.getAttribute('data-wk-contrast')) return;
    var bg = parseColor(box.style.getPropertyValue('--box-color'));
    var fg = parseColor(box.style.getPropertyValue('--box-text-color'));
    if (!bg || !fg) return;
    box.setAttribute('data-wk-contrast', 'checked');
    if (contrast(fg, bg) >= MIN_RATIO) return;

    var target = luminance(bg) > 0.18 ? 0 : 255;
    for (var step = 0; step < 40 && contrast(fg, bg) < TARGET_RATIO; step++) {
      fg = fg.map(function (v) { return Math.round(v + (target - v) * 0.05); });
    }
    box.style.setProperty('--box-text-color', fg.join(', '));
    box.setAttribute('data-wk-contrast', 'adjusted');
  }

  function labelThumbnails(root) {
    var lists = root.querySelectorAll('.thumbnails');
    Array.prototype.forEach.call(lists, function (list) {
      var links = list.querySelectorAll('a.thumbnail');
      Array.prototype.forEach.call(links, function (link, index) {
        if (link.hasAttribute('aria-label') || link.hasAttribute('aria-labelledby')) return;
        if (link.textContent.trim()) return;
        if (link.querySelector('img[alt]:not([alt=""])')) return;
        link.setAttribute('aria-label', 'View image ' + (index + 1));
      });
    });
  }

  function run(root) {
    var boxes = root.querySelectorAll('.text-box[style*="--box-text-color"]');
    Array.prototype.forEach.call(boxes, fixTextBox);
    labelThumbnails(root);
  }

  function relevant(node) {
    return node.nodeType === 1
      && (node.matches('.text-box, .thumbnails, a.thumbnail') || node.querySelector('.text-box, .thumbnails'));
  }

  function init() {
    run(document);
    if (!window.MutationObserver) return;

    var queued = false;
    new MutationObserver(function (mutations) {
      if (queued) return;
      var hit = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, relevant);
      });
      if (!hit) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        run(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
